import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {FormValidationService} from '../../services/form-validation/form-validation.service';

type TimeType = 'am' | 'pm';

export interface IError {
    message: string;
}

@Component({
    selector: 'npa-time',
    templateUrl: './npa-time.component.html',
    styleUrls: ['./npa-time.component.styl']
})
export class NpaTimeComponent implements OnInit, OnChanges {
    @Input() label: string;
    timeLabel: string;
    @Input() lang: string;
    @Input() valueChange;
    @Input() required = false as boolean;
    @Input() readonly: boolean;
    timeType: TimeType;
    @Input() config = {
        format: 'HH:mm am/pm',
        appearance: 'outline'
    };

    validation: any = {
        format: {
            prs: 'زمان باید به فارمت hh:mm باشد',
            en: 'Please follow the format hh:mm',
            ps: 'وخت باید په hh:mm فارت کې وي'
        },
        required: {
            prs: 'این معلومات الزامی است',
            en: 'This information is required',
            ps: 'دغه مالومات اړین ده'
        },
        value: {
            prs: 'زمان وارد شده اشتباه میباشد',
            en: 'The entered time is incorrect',
            ps: 'زمان وارد شده اشتباه میباشد'
        }
    };

    @Input() model: FormControl;
    @Output() modelChange: EventEmitter<Date | any> = new EventEmitter<Date | any>();

    @ViewChild('hourInput', {static: false}) hourInput: ElementRef;
    @ViewChild('minuteInput', {static: false}) minuteInput: ElementRef;
    error: IError = {message: undefined};

    timeValue: string;
    hoursValue: string;
    minutesValue: string;

    isDirty: boolean;
    isFocused: boolean;

    constructor(
        public formValidationService: FormValidationService,
    ) {
    }

    ngOnInit(): void {
        this.changeFormat();
        this.initReadOnlyState();
    }

    setInputFocus(element: ElementRef | any) {
        this.isFocused = true;
        if (element && element.nativeElement) {
            element.nativeElement.focus();
        } else if (element) {
            element.focus();
        }
    }

    changeFormat() {
        this.config.format = 'H:m am/pm';
    }

    private initReadOnlyState() {
        this.readonly = this.model.disabled || this.readonly;
    }

    formatAMPM(time: string) {
        const timeArray = time.split(':');
        let hours = +timeArray[0];
        const minutes = +timeArray[1];
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return (hours < 10 ? '0' + hours : hours) + ':' + (minutes < 10 ? '0' + minutes : minutes) + ' ' + ampm;
    }


    ngOnChanges(changes: SimpleChanges): void {
        this.timeLabel = this.label;
        this.timeType = this.timeType === undefined ? 'am' : this.timeType;

        if (this.model && (this.model.value === undefined || this.model.value === '')) {
            return;
        }
        if (changes.valueChange && changes.valueChange.currentValue &&
            /^([1-9]|0[1-9]|1[0-9]|2[0-3])([:])([0-9]|0[0-9]|[1-5][0-9])([:])([0-9]|0[0-9]|[1-5][0-9])$/i.test(changes.valueChange.currentValue)) {
            const time12Hours = this.formatAMPM(changes.valueChange.currentValue);
            this.timeValue = time12Hours.split(' ')[0];
            const timeParts = this.timeValue.split(':');
            this.hoursValue = timeParts[0];
            this.minutesValue = timeParts[1];
            this.timeType = time12Hours.split(' ')[1] as TimeType;
        }
    }

    hourInputChange($event) {
        const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if ($event.key === 'Tab' || $event.key === ':') {
            this.hourInput.nativeElement.focus();
        }
        if ($event.key === 'Backspace') {
            this.hoursValue = this.hoursValue.slice(0, -1);
        }
        if (DIGITS.indexOf($event.key) === -1) {
            return;
        }
        const hasManuallyEnteredZero = $event.key === '0';

        const INITIAL_DIGIT = '0';
        const hours = $event.key;
        if (this.hoursValue && this.hoursValue.length > 1) {
            this.hoursValue = this.hoursValue.slice(1) + hours;
        } else {
            this.hoursValue = INITIAL_DIGIT + hours;
        }
        this.timeType = +this.hoursValue > 12 && +this.hoursValue < 24 && this.timeType === 'am' ?
            'pm' : (+this.hoursValue === 12 ? this.timeType : 'am');
        // if (this.hoursValue.length === 2 && hasManuallyEnteredZero) {
        //     this.minuteInput.nativeElement.focus();
        // }
        this.markInputDirty();
        return false;
    }

    minuteInputChange($event) {
        const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if ($event.key === 'Backspace') {
            this.minutesValue = this.minutesValue.slice(0, -1);
        }
        if (DIGITS.indexOf($event.key) === -1) {
            return;
        }
        const INITIAL_DIGIT = '0';
        const minutes = $event.key;
        if (this.minutesValue && this.minutesValue.length > 1) {
            this.minutesValue = this.minutesValue.slice(1) + minutes;
        } else {
            this.minutesValue = INITIAL_DIGIT + minutes;
        }
        this.markInputDirty();
        return false;
    }

    onChange() {
        const timeValue = this.hoursValue + ':' + this.minutesValue;

        if (!this.isTimeValid(timeValue)) {
            this.error = {message: this.validation.value[this.lang]};
            console.error('time format is INVALID');
            return;
        }
        if (+this.hoursValue > 24 || +this.minutesValue > 59) {
            this.error = {message: this.validation.value[this.lang]};
            console.error('time is INVALID');
            return;
        }
        this.error = undefined;
        if (timeValue && this.isTimeValid(timeValue)) {
            this.timeValue = this.changeTo24hours(timeValue);
            this.model.setValue(this.timeValue);
            this.modelChange.emit(this.model);
        } else {
            this.model.setValue(undefined);
            this.modelChange.emit(this.model);
        }
    }

    changeType(typeSlug: TimeType) {
        this.timeType = typeSlug;
        this.onChange();
    }

    markInputDirty() {
        if (this.hourInput.nativeElement.value !== this.hourInput.nativeElement.defaultValue
            || this.minuteInput.nativeElement.value !== this.minuteInput.nativeElement.defaultValue) {
            this.isDirty = true;
        }
    }

    isTimeValid(value: string): boolean {
        return /^([1-9]|0[1-9]|1[012])([:])([0-9]|0[0-9]|[1-5][0-9])$/i.test(value);
    }

    adjustHours() {
        if (+this.hoursValue > 12 && +this.hoursValue < 24) {
            const offsetHours = +this.hoursValue - 12;
            this.hoursValue = (offsetHours < 10 ? '0' + offsetHours : offsetHours).toString();
        }
    }

    changeTo24hours(time: string) {
        const timeArray = time.split(':');
        const hours = +timeArray[0];
        const minutes = +timeArray[1];
        let timeString = time;
        if (this.timeType === 'pm') {
            if (hours === 12) {
                timeString = '12:' + (minutes < 10 ? '0' + minutes : minutes);
            } else {
                timeString = '' + (hours > 12 ? hours : hours + 12) + ':' + (minutes < 10 ? '0' + minutes : minutes);
            }
        } else if (this.timeType === 'am') {
            if (hours === 12) {
                timeString = '00:' + (minutes < 10 ? '0' + minutes : minutes);
            } else if (hours < 12) {
                timeString = (hours < 10 ? '0' + hours : hours) + ':' + (minutes < 10 ? '0' + minutes : minutes);
            }
        }
        return timeString;
    }
}
